import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { CPF_MASK, CNPJ_MASK, PHONE_MASK, PIS_MASK } from 'v3/utils/masks';

import ApplicationLayout from 'v3/layouts/Application';
import Card from 'v3/components/Card';
import { Button, Text } from 'v3/components';
import Input from 'v3/components/Input';
import Select from 'v3/components/Select';
import DatePicker from 'v3/components/DatePicker';
import InputAddress from 'v3/components/InputAddress';
import api from 'services/api';
import { useSnackbar } from 'v3/components/Snackbar';
import {
  approveShippingCompanyRequest,
  getShippingCompanyRequest,
  rejectShippingCompanyRequest,
  reopenShippingCompanyRequest,
  startShippingCompanyRequestAnalysis,
} from 'v3/services/shippingCompanyRequest';

import { autonomousOptions, rntrcOwnOptions, shippingTypeOptions } from '../mockData';
import { fromApiDate, toApiDate } from 'v3/utils/rntrcValidation';
import {
  EMISSION_START_DATE_REQUIRED,
  REJECT_REASON_MAX,
  REJECT_REASON_REQUIRED,
  validateForm,
} from './validator';
import AccountantCard from '../shared/AccountantCard';
import { formatSimplesNacional } from '../shared/formatters';
import { STATUS, isEditableStatus } from '../shared/status';
import useAllowedActions from '../shared/useAllowedActions';
import StatusPanel from './StatusPanel';
import StatusActions, { HeaderStatusActions } from './StatusActions';
import RejectModal from './RejectModal';
import EmissionStartDateModal from './EmissionStartDateModal';
import AuditModal from './AuditModal';
import ReadOnlyData from './ReadOnlyData';
import ShippingCompanyLink from './ShippingCompanyLink';

const FIELD_ERRORS = [REJECT_REASON_REQUIRED, REJECT_REASON_MAX, EMISSION_START_DATE_REQUIRED];

function getDocumentMask(value, isAutonomous) {
  if (isAutonomous) return CPF_MASK;
  const chars = (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return chars.length <= 11 ? CPF_MASK : CNPJ_MASK;
}

export default function ShippingCompanyRequestDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const inputAddressRef = useRef();

  const snackbar = useSnackbar();

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loadKey, setLoadKey] = useState(0);
  const [pendingAction, setPendingAction] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [emissionOpen, setEmissionOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [documents, setDocuments] = useState([]);

  // Antes dos early returns de `loading` e `!request`: `useAllowedActions` é hook e tem de ser chamado
  // em todo render. Devolve as ações do status já sem as que o usuário não pode executar (US12).
  const slug = request?.status?.slug;
  const actions = useAllowedActions(slug);

  function mapDataToForm(data) {
    return {
      userName: data.userName || '',
      userDocument: data.userDocument || '',
      userEmail: data.userEmail || '',
      userPhone: data.userPhone || '',
      shippingName: data.shippingName || '',
      shippingDocument: data.shippingDocument || '',
      shippingStateRegistration: data.shippingStateRegistration || '',
      shippingPhone: data.shippingPhone || '',
      shippingEmail: data.shippingEmail || '',
      autonomous:
        autonomousOptions.find((o) => o.value === data.autonomous) || autonomousOptions[1],
      shippingType: shippingTypeOptions.find((o) => o.value === data.anttType?.id) || null,
      address: data.addressStreet || '',
      addressNumber: data.addressNumber || '',
      addressNeighborhood: data.addressDistrict || '',
      addressComplement: data.addressComplement || '',
      addressZipCode: data.addressCep || '',
      country: data.country || null,
      addressCity: data.city || null,
      addressProvince: data.province || null,
      anttNumber: data.anttRntrcNumber || '',
      anttIssueDate: fromApiDate(data.anttIssueDate),
      anttExpDate: fromApiDate(data.anttExpDate),
      anttType: shippingTypeOptions.find((o) => o.value === data.anttType?.id) || null,
      rntrcOwn: rntrcOwnOptions.find((o) => o.value === data.anttIsOwner) || rntrcOwnOptions[0],
      anttTransporterDocument: data.anttDocument || '',
      shippingPis: data.pis || '',
    };
  }

  const [countryOptions, setCountryOptions] = useState([]);
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [disableNumber, setDisableNumber] = useState(true);
  const [disableNeighborhood, setDisableNeighborhood] = useState(true);
  const [disableZipCode, setDisableZipCode] = useState(true);
  const [disableCity, setDisableCity] = useState(true);
  const [disableProvince, setDisableProvince] = useState(true);

  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  function handleChange(field) {
    return ({ target }) => setFormData((prev) => ({ ...prev, [field]: target.value }));
  }

  function handleSelectChange(field) {
    return (value) => setFormData((prev) => ({ ...prev, [field]: value }));
  }

  async function fetchCities(search, uf = '', country = '') {
    try {
      const response = await api.get('cities', { params: { search, uf, country } });
      return response.data;
    } catch {
      return [];
    }
  }

  function disableAddressFields(address = {}) {
    setDisableNumber(!!address.number);
    setDisableNeighborhood(!!address.neighborhood);
    setDisableZipCode(!!address.zip_code);
    setDisableCity(!!address?.city);
    setDisableProvince(!!address?.city?.province?.uf || !!address?.province);
  }

  async function handleSelectedAddress(place) {
    const [city] = await fetchCities(place.city, place.province, place.country_abbreviation);
    const provinceUf = city?.province?.uf || place.province || '';
    const matchedProvince =
      provinceOptions.find((p) => p.uf?.toLowerCase() === provinceUf?.toLowerCase()) || null;
    setFormData((prev) => ({
      ...prev,
      address: place?.address || '',
      addressNumber: place?.number || '',
      addressComplement: place?.name || '',
      addressZipCode: place?.zip_code || '',
      addressNeighborhood: place?.neighborhood || '',
      addressCity: city || null,
      addressProvince: matchedProvince,
    }));
    disableAddressFields({
      number: place.number,
      neighborhood: place.neighborhood,
      zip_code: place.zip_code,
      city: city || null,
      province: provinceUf,
    });
  }

  // O formulário só é remapeado na primeira carga, para não descartar o que o operador já ajustou.
  async function loadRequest({ remapForm }) {
    const data = await getShippingCompanyRequest(id);
    setRequest(data);
    if (remapForm) {
      setFormData(mapDataToForm(data));
      setDocuments(data.documents || []);
      setDisableNumber(!!data.addressNumber);
      setDisableNeighborhood(!!data.addressDistrict);
      setDisableZipCode(false);
      setDisableCity(!!data.city);
      setDisableProvince(!!data.province);
    }
  }

  async function reloadRequest() {
    try {
      await loadRequest({ remapForm: false });
    } catch (error) {
      snackbar.show(error.message, { type: 'error' });
    }
  }

  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    loadRequest({ remapForm: true })
      .catch((error) => {
        setRequest(null);
        setLoadError(error);
      })
      .finally(() => setLoading(false));
  }, [id, loadKey]);

  useEffect(() => {
    async function loadOptions() {
      try {
        const [countriesRes, provincesRes] = await Promise.all([
          api.get('countries'),
          api.get('provinces'),
        ]);
        const countries = countriesRes.data || [];
        const provinces = provincesRes.data || [];
        setCountryOptions(countries);
        setProvinceOptions(provinces);
        setFormData((prev) => ({
          ...prev,
          country:
            prev.country || countries.find((c) => c.abbreviation?.toLowerCase() === 'br') || null,
        }));
      } catch {
        // ignore
      }
    }
    loadOptions();
  }, []);

  if (loading) {
    return (
      <ApplicationLayout title="Detalhes da Solicitação">
        <Text>Carregando...</Text>
      </ApplicationLayout>
    );
  }

  if (!request) {
    // 404 (ou resposta vazia) é solicitação inexistente; os demais erros mostram a mensagem e permitem tentar de novo.
    const notFound = !loadError || loadError.status === 404;
    return (
      <ApplicationLayout title="Detalhes da Solicitação">
        {notFound ? (
          <Text>Solicitação não encontrada.</Text>
        ) : (
          <div className="d-flex align-items-center" data-testid="request-load-error">
            <Text type="regular" color="danger" className="mr-3">
              {loadError.message}
            </Text>
            <Button variant="secondary" size="sm" onClick={() => setLoadKey((key) => key + 1)}>
              <Text color="dark" weight={500}>
                Tentar novamente
              </Text>
            </Button>
          </div>
        )}
      </ApplicationLayout>
    );
  }

  // A partir da aprovação o backend devolve `shippingCompanyId`, com os campos já resolvidos do cadastro
  // vigente da transportadora (RN-002) — é ele que decide a visão somente leitura. O `readOnly` do payload
  // só suprime o formulário num status ainda editável, tolerando o front subir antes da API.
  const editableStatus = isEditableStatus(slug);
  const showReadOnly = !!request.shippingCompanyId;
  const showDataCards = editableStatus && request.readOnly !== true && !request.shippingCompanyId;
  // Pós-aprovação sem transportadora vinculada (CNPJ gerido ou vínculo removido): não há cadastro a consultar.
  const showNoCompany = !editableStatus && slug !== STATUS.REJECTED && !request.shippingCompanyId;

  async function handleApprove() {
    const hasDuplicate = documents.some((doc, i) => {
      const chars = (doc.document || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
      return (
        chars.length === 14 &&
        documents.some(
          (d, j) => j !== i && (d.document || '').toUpperCase().replace(/[^A-Z0-9]/g, '') === chars,
        )
      );
    });

    if (hasDuplicate) {
      snackbar.show('Corrija os CNPJs duplicados antes de aprovar.', { type: 'error' });
      return;
    }

    const isValid = await validateForm(formData, setErrors);
    if (!isValid) {
      snackbar.show('Corrija os erros antes de aprovar.', { type: 'error' });
      return;
    }

    const payload = {
      userName: formData.userName,
      userDocument: formData.userDocument,
      userEmail: formData.userEmail,
      userPhone: formData.userPhone,
      shippingName: formData.shippingName,
      shippingDocument: formData.shippingDocument,
      shippingStateRegistration: formData.shippingStateRegistration || null,
      shippingPhone: formData.shippingPhone,
      shippingEmail: formData.shippingEmail,
      addressStreet: formData.address,
      addressNumber: formData.addressNumber,
      addressDistrict: formData.addressNeighborhood,
      addressComplement: formData.addressComplement || null,
      addressCep: formData.addressZipCode || null,
      addressCountryId: formData.country?.id || null,
      addressCityId: formData.addressCity?.id || null,
      addressProvinceId: formData.addressProvince?.id || null,
      addressCityName: formData.addressCity?.name || null,
      addressProvinceName: formData.addressProvince?.uf || null,
      anttRntrcNumber: formData.anttNumber,
      anttIssueDate: toApiDate(formData.anttIssueDate),
      anttExpDate: toApiDate(formData.anttExpDate),
      anttTypeId: formData.anttType?.value || null,
      anttIsOwner: formData.rntrcOwn?.value ?? true,
      anttDocument: formData.anttTransporterDocument || null,
      pis: formData.shippingPis || null,
      documents: documents.map((d) => ({ id: d.id, document: d.document })),
    };

    await runAction(
      'approveData',
      () => approveShippingCompanyRequest(id, { ...payload, fromStatus: slug }),
      'Solicitação aprovada com sucesso!',
    );
  }

  // Executa uma mudança de status. Nos modais, o erro de campo (422) volta para o formulário.
  async function runAction(name, action, successMessage, { fromModal = false } = {}) {
    setPendingAction(name);
    try {
      await action();
      snackbar.show(successMessage, { type: 'success' });
      await reloadRequest();
      return { ok: true };
    } catch (error) {
      if (fromModal && error.status === 422 && FIELD_ERRORS.includes(error.message)) {
        return { fieldError: error.message };
      }
      snackbar.show(error.message, { type: 'error' });
      if (error.status === 409) await reloadRequest();
      return { ok: false, conflict: error.status === 409 };
    } finally {
      setPendingAction(null);
    }
  }

  function handleStartAnalysis() {
    return runAction(
      'startAnalysis',
      () => startShippingCompanyRequestAnalysis(id, { fromStatus: slug }),
      'Solicitação em análise documental',
    );
  }

  function handleReopen() {
    return runAction(
      'reopen',
      () => reopenShippingCompanyRequest(id, { fromStatus: slug }),
      'Solicitação voltou para análise documental',
    );
  }

  async function handleRejectConfirm(reason) {
    const result = await runAction(
      'reject',
      () => rejectShippingCompanyRequest(id, { fromStatus: slug, reason }),
      'Solicitação reprovada',
      { fromModal: true },
    );
    if (result.ok || result.conflict) setRejectOpen(false);
    return result;
  }

  async function handleEmissionConfirm(emissionStartDate) {
    const result = await runAction(
      'approveEmission',
      () => approveShippingCompanyRequest(id, { fromStatus: slug, emissionStartDate }),
      'Cadastro para emissão aprovado',
      { fromModal: true },
    );
    if (result.ok || result.conflict) setEmissionOpen(false);
    return result;
  }

  return (
    <ApplicationLayout title="Detalhes da Solicitação">
      <StatusPanel
        request={request}
        onOpenAudit={() => setAuditOpen(true)}
        headerActions={
          <>
            <ShippingCompanyLink shippingCompanyId={request.shippingCompanyId} />
            <HeaderStatusActions
              actions={actions}
              pending={pendingAction}
              onStartAnalysis={handleStartAnalysis}
              onReject={() => setRejectOpen(true)}
            />
          </>
        }
      />

      {showDataCards && (
        <>
          {/* ── Dados do usuário ── */}
          <Card
            header={
              <Text color="dark" type="header">
                Dados do usuário para acesso
              </Text>
            }
          >
            <Row className="mt-3">
              <Col md={6} xs={12} className="mb-3">
                <Input
                  label="Nome Completo *"
                  placeholder="Seu nome completo"
                  value={formData.userName}
                  onChange={handleChange('userName')}
                  error={errors?.userName}
                />
              </Col>
              <Col md={6} xs={12} className="mb-3">
                <Input
                  label="CPF do Usuário *"
                  placeholder="Seu CPF"
                  masked
                  mask={CPF_MASK}
                  value={formData.userDocument}
                  onChange={handleChange('userDocument')}
                  error={errors?.userDocument}
                />
              </Col>
              <Col md={6} xs={12} className="mb-3">
                <Input
                  label="E-mail do Usuário *"
                  placeholder="seu.email@empresa.com.br"
                  value={formData.userEmail}
                  onChange={handleChange('userEmail')}
                  error={errors?.userEmail}
                />
              </Col>
              <Col md={6} xs={12} className="mb-3">
                <Input
                  label="Telefone *"
                  placeholder="(xx) xxxxx-xxxx"
                  masked
                  mask={PHONE_MASK}
                  value={formData.userPhone}
                  onChange={handleChange('userPhone')}
                  error={errors?.userPhone}
                />
              </Col>
            </Row>
          </Card>

          <Card
            header={
              <Text color="dark" type="header">
                Dados da transportadora
              </Text>
            }
          >
            <Row className="mt-3">
              <Col md={6} xs={12} className="mb-3">
                <Select
                  label="Autônomo *"
                  options={autonomousOptions}
                  value={formData.autonomous}
                  onChange={handleSelectChange('autonomous')}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  disableClear
                  error={errors?.autonomous}
                />
              </Col>
              <Col md={6} xs={12} className="mb-3">
                <Input
                  label={formData.autonomous?.value ? 'CPF *' : 'CNPJ *'}
                  placeholder={formData.autonomous?.value ? '000.000.000-00' : 'AA.AAA.AAA/AAAA-00'}
                  masked
                  mask={formData.autonomous?.value ? CPF_MASK : CNPJ_MASK}
                  value={formData.shippingDocument}
                  onChange={handleChange('shippingDocument')}
                  error={errors?.shippingDocument}
                />
              </Col>
              {formData.autonomous?.value && (
                <Col md={6} xs={12} className="mb-3">
                  <Input
                    label="PIS *"
                    placeholder="Informe seu PIS"
                    masked
                    mask={PIS_MASK}
                    value={formData.shippingPis}
                    onChange={handleChange('shippingPis')}
                    error={errors?.shippingPis}
                  />
                </Col>
              )}
              <Col md={6} xs={12} className="mb-3">
                <Input
                  label="Razão Social *"
                  placeholder="Informe a razão social"
                  value={formData.shippingName}
                  onChange={handleChange('shippingName')}
                  error={errors?.shippingName}
                />
              </Col>
              {!formData.autonomous?.value && (
                <Col md={6} xs={12} className="mb-3">
                  <Input
                    label="Inscrição Estadual *"
                    placeholder="Informe a inscrição estadual"
                    value={formData.shippingStateRegistration}
                    onChange={handleChange('shippingStateRegistration')}
                    error={errors?.shippingStateRegistration}
                  />
                </Col>
              )}
              <Col md={6} xs={12} className="mb-3">
                <Input
                  label="Telefone *"
                  placeholder="(xx) xxxxx-xxxx"
                  masked
                  mask={PHONE_MASK}
                  value={formData.shippingPhone}
                  onChange={handleChange('shippingPhone')}
                  error={errors?.shippingPhone}
                />
              </Col>
              <Col md={6} xs={12} className="mb-3">
                <Input
                  label="E-mail *"
                  placeholder="seu.email@empresa.com.br"
                  value={formData.shippingEmail}
                  onChange={handleChange('shippingEmail')}
                  error={errors?.shippingEmail}
                />
              </Col>
              <Col md={6} xs={12} className="mb-3">
                <Select
                  label="Tipo *"
                  options={shippingTypeOptions}
                  value={formData.shippingType}
                  onChange={handleSelectChange('shippingType')}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  error={errors?.shippingType}
                />
              </Col>
              <Col md={4} xs={12} className="mb-3">
                <Text type="label" color="dark">
                  Optante Simples Nacional
                </Text>
                <Text type="regular" color="gray" as="p" className="mt-2">
                  {formatSimplesNacional(request?.shippingSimplesNacional)}
                </Text>
              </Col>
            </Row>
          </Card>

          {/* ── Dados contador ── */}
          <AccountantCard
            name={request?.accountantName}
            phone={request?.accountantPhone}
            email={request?.accountantEmail}
          />

          {/* ── Endereço ── */}
          <Card
            header={
              <Text color="dark" type="header">
                Endereço
              </Text>
            }
          >
            <Row className="mt-3">
              <Col md={4} xs={12} className="mb-3">
                <Select
                  label="País *"
                  placeholder="Informe o país"
                  options={countryOptions}
                  value={formData.country}
                  onChange={(value) => setFormData((prev) => ({ ...prev, country: value }))}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  error={errors?.country}
                />
              </Col>
              <Col md={8} xs={12} className="mb-3">
                <InputAddress
                  ref={inputAddressRef}
                  label="Logradouro *"
                  placeholder="Informe o endereço"
                  value={formData.address || ''}
                  onChange={handleChange('address')}
                  onSelectedAddress={handleSelectedAddress}
                  country={formData.country?.abbreviation || 'br'}
                  error={errors?.address}
                />
              </Col>
              <Col md={3} xs={12} className="mb-3">
                <Input
                  label="Número *"
                  placeholder="Informe o número"
                  disabled={disableNumber}
                  value={formData.addressNumber}
                  onChange={handleChange('addressNumber')}
                  error={errors?.addressNumber}
                />
              </Col>
              <Col md={5} xs={12} className="mb-3">
                <Input
                  label="Bairro *"
                  placeholder="Informe o bairro"
                  disabled={disableNeighborhood}
                  value={formData.addressNeighborhood}
                  onChange={handleChange('addressNeighborhood')}
                  error={errors?.addressNeighborhood}
                />
              </Col>
              <Col md={4} xs={12} className="mb-3">
                <Input
                  label="CEP"
                  placeholder="Informe o CEP"
                  disabled={disableZipCode}
                  value={formData.addressZipCode}
                  onChange={handleChange('addressZipCode')}
                  error={errors?.addressZipCode}
                />
              </Col>
              <Col md={6} xs={12} className="mb-3">
                <Select.Async
                  label="Cidade *"
                  placeholder="Informe a cidade"
                  isDisabled={disableCity}
                  onSearch={(city) => fetchCities(city, '', formData.country?.abbreviation || 'br')}
                  value={formData.addressCity}
                  onChange={(city) =>
                    setFormData((prev) => ({
                      ...prev,
                      addressCity: city,
                      addressProvince: city?.province?.uf || prev.addressProvince,
                    }))
                  }
                  getOptionLabel={(option) =>
                    `${option.name}${option.province?.uf ? ` - ${option.province.uf}` : ''}`
                  }
                  getOptionValue={(option) => option.id}
                  error={errors?.addressCity}
                />
              </Col>
              <Col md={3} xs={12} className="mb-3">
                <Select
                  label="Estado *"
                  placeholder="Informe o estado"
                  isDisabled={disableProvince}
                  options={provinceOptions}
                  value={formData.addressProvince}
                  onChange={(opt) =>
                    setFormData((prev) => ({ ...prev, addressProvince: opt || null }))
                  }
                  getOptionLabel={(option) => `${option.name} (${option.uf})`}
                  getOptionValue={(option) => option.id}
                  error={errors?.addressProvince}
                />
              </Col>
              <Col md={3} xs={12} className="mb-3">
                <Input
                  label="Complemento"
                  placeholder="Informe o complemento"
                  value={formData.addressComplement}
                  onChange={handleChange('addressComplement')}
                  error={errors?.addressComplement}
                />
              </Col>
            </Row>
          </Card>

          {/* ── ANTT ── */}
          <Card
            header={
              <Text color="dark" type="header">
                ANTT / RNTRC
              </Text>
            }
          >
            <Row className="mt-3">
              <Col md={4} xs={12} className="mb-3">
                <Input
                  label="Número RNTRC *"
                  placeholder="00000000"
                  value={formData.anttNumber}
                  onChange={handleChange('anttNumber')}
                  error={errors?.anttNumber}
                />
              </Col>
              <Col md={4} xs={12} className="mb-3">
                <DatePicker
                  guide
                  label="Data de Emissão *"
                  value={formData.anttIssueDate}
                  onChange={handleChange('anttIssueDate')}
                  error={errors?.anttIssueDate}
                />
              </Col>
              <Col md={4} xs={12} className="mb-3">
                <DatePicker
                  guide
                  label="Data de Validade"
                  value={formData.anttExpDate}
                  onChange={handleChange('anttExpDate')}
                  error={errors?.anttExpDate}
                />
              </Col>
              <Col md={4} xs={12} className="mb-3">
                <Select
                  label="Tipo *"
                  placeholder="Selecione o tipo"
                  options={shippingTypeOptions}
                  value={formData.anttType}
                  onChange={handleSelectChange('anttType')}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  error={errors?.anttType}
                />
              </Col>
              <Col md={4} xs={12} className="mb-3">
                <Select
                  label="RNTRC *"
                  options={rntrcOwnOptions}
                  value={formData.rntrcOwn}
                  onChange={handleSelectChange('rntrcOwn')}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  disableClear
                  error={errors?.rntrcOwn}
                />
              </Col>
              {!formData.rntrcOwn?.value && (
                <Col md={8} xs={12} className="mb-3">
                  <Input
                    label="CPF / CNPJ do transportador *"
                    placeholder={
                      formData.autonomous?.value
                        ? 'Informe o CPF do motorista'
                        : 'Informe o CNPJ do transportador'
                    }
                    masked
                    mask={getDocumentMask(formData.anttTransporterDocument, false)}
                    value={formData.anttTransporterDocument}
                    onChange={handleChange('anttTransporterDocument')}
                    error={errors?.anttTransporterDocument}
                  />
                </Col>
              )}
            </Row>
          </Card>
          {!formData.autonomous?.value && (
            <Card
              header={
                <Text color="dark" type="header">
                  CNPJs vinculados
                </Text>
              }
            >
              <Row className="mt-3">
                {documents.map((doc, index) => {
                  const chars = (doc.document || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
                  const isDuplicate =
                    chars.length === 14 &&
                    documents.some(
                      (d, i) =>
                        i !== index &&
                        (d.document || '').toUpperCase().replace(/[^A-Z0-9]/g, '') === chars,
                    );
                  return (
                    <Col key={doc.id ?? `new-${index}`} xs={12} className="mb-3">
                      <div className="d-flex align-items-end" style={{ gap: '0.5rem' }}>
                        <div style={{ flex: 1 }}>
                          <Input
                            label="CNPJ"
                            placeholder="AA.AAA.AAA/AAAA-00"
                            masked
                            mask={CNPJ_MASK}
                            value={doc.document}
                            error={isDuplicate ? 'CNPJ já adicionado' : undefined}
                            onChange={({ target }) =>
                              setDocuments((prev) =>
                                prev.map((d, i) =>
                                  i === index ? { ...d, document: target.value } : d,
                                ),
                              )
                            }
                          />
                        </div>
                        <Button
                          variant="error"
                          size="sm"
                          style={{ marginBottom: isDuplicate ? '20px' : '4px' }}
                          onClick={() => setDocuments((prev) => prev.filter((_, i) => i !== index))}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </Col>
                  );
                })}
              </Row>
              <Button
                variant="success"
                size="sm"
                disabled={documents.some((d) => !(d.document || '').trim())}
                onClick={() => setDocuments((prev) => [...prev, { document: '' }])}
              >
                <FaPlus />
                <Text color="white" weight={500} type="regular" style={{ marginLeft: '0.4rem' }}>
                  Adicionar CNPJ
                </Text>
              </Button>
            </Card>
          )}
        </>
      )}

      {showReadOnly && <ReadOnlyData request={request} />}

      {showNoCompany && (
        <Card>
          <Text type="regular" color="gray" as="p" data-testid="request-without-company">
            Esta solicitação não tem uma transportadora vinculada, então os dados do cadastro não
            podem ser exibidos aqui.
          </Text>
        </Card>
      )}

      <StatusActions
        actions={actions}
        pending={pendingAction}
        onBack={() => navigate('/transportadoras/solicitacoes')}
        onStartAnalysis={handleStartAnalysis}
        onApproveData={handleApprove}
        onApproveEmission={() => setEmissionOpen(true)}
        onReject={() => setRejectOpen(true)}
        onReopen={handleReopen}
      />

      <RejectModal
        show={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onConfirm={handleRejectConfirm}
      />
      <EmissionStartDateModal
        show={emissionOpen}
        onClose={() => setEmissionOpen(false)}
        onConfirm={handleEmissionConfirm}
      />
      <AuditModal show={auditOpen} requestId={id} onClose={() => setAuditOpen(false)} />
    </ApplicationLayout>
  );
}
